import { Download, X } from "lucide-react";
import { useState, type ChangeEvent, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { toast } from "@/shared/lib/toast";
import { Drawer } from "@/shared/ui/drawer";
import { Spinner } from "@/shared/ui/spinner";

import { useReturn } from "../api/use-return";
import { useReturns, type ReturnFilters } from "../api/use-returns";
import { ReturnDetail } from "../components/ReturnDetail";
import { ReturnsTable } from "../components/ReturnsTable";
import { MARKETPLACE_LABEL } from "../lib/marketplace-labels";
import {
  hasActiveReturnFilters,
  mergeReturnFilter,
  resetReturnPage,
  type ReturnFilterUpdate,
} from "../lib/return-url-state";
import type { Marketplace } from "../lib/types";

interface KpiCardProps {
  label: string;
  value: string | number;
  alert?: boolean;
}

function KpiCard({ label, value, alert = false }: KpiCardProps): JSX.Element {
  return (
    <div
      className="flex flex-col gap-1 rounded-[14px] border border-[var(--premium-border)] bg-[var(--surface-raised)] p-4 shadow-[var(--sh-card)]"
      data-testid="returns-kpi-card"
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
        {label}
      </span>
      <span
        className={
          alert
            ? "text-2xl font-extrabold tabular-nums text-[var(--intent-warning-fg)]"
            : "text-2xl font-extrabold tabular-nums text-[var(--text-primary)]"
        }
      >
        {value}
      </span>
    </div>
  );
}

// Inner component that fetches + renders the return detail inside the drawer.
function DrawerContent({ returnId }: { returnId: string }): JSX.Element {
  const { t } = useTranslation("orders");
  const { data: returnRec, isLoading } = useReturn(returnId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" label={t("loading_orders")} />
      </div>
    );
  }

  if (!returnRec) {
    return (
      <div className="px-5 py-8 text-sm text-[var(--text-subtle)]">
        {t("empty_order_not_found")}
      </div>
    );
  }

  return <ReturnDetail returnRec={returnRec} />;
}

/** Returns a local YYYY-MM-DD string (NOT UTC) to avoid off-by-one in Asia/Bangkok. */
function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

interface ReturnsPageProps {
  urlFilter: ReturnFilters;
  onUrlFilterChange: (next: ReturnFilters) => void;
  initialReturnId?: string;
}

export function ReturnsPage({
  urlFilter,
  onUrlFilterChange,
  initialReturnId,
}: ReturnsPageProps): JSX.Element {
  const { t } = useTranslation("orders");

  const [selectedReturnId, setSelectedReturnId] = useState<string | undefined>(initialReturnId);

  const { data, isLoading } = useReturns(urlFilter);
  const returns = data?.returns ?? [];
  const total = data?.total ?? 0;

  const limit = urlFilter.limit ?? 20;
  const offset = urlFilter.offset ?? 0;

  function handleFilterChange(updates: ReturnFilterUpdate): void {
    onUrlFilterChange(resetReturnPage(mergeReturnFilter(urlFilter, updates)));
  }

  function handleClear(): void {
    onUrlFilterChange({});
  }

  function handleSearchCommit(e: { currentTarget: HTMLInputElement }): void {
    const v = e.currentTarget.value.trim();
    handleFilterChange({ search: v || undefined });
  }

  function handleMarketplaceChange(e: ChangeEvent<HTMLSelectElement>): void {
    const v = e.target.value;
    handleFilterChange({ marketplace: v === "all" ? undefined : v });
  }

  function handleDateFromChange(e: ChangeEvent<HTMLInputElement>): void {
    const v = e.target.value;
    handleFilterChange(v ? { date_from: v } : { date_from: undefined });
  }

  function handleDateToChange(e: ChangeEvent<HTMLInputElement>): void {
    const v = e.target.value;
    handleFilterChange(v ? { date_to: v } : { date_to: undefined });
  }

  function handlePresetToday(): void {
    const today = toLocalYmd(new Date());
    handleFilterChange({ date_from: today, date_to: today });
  }

  function handlePreset7d(): void {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 6);
    handleFilterChange({ date_from: toLocalYmd(from), date_to: toLocalYmd(today) });
  }

  function handlePresetMonth(): void {
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    handleFilterChange({ date_from: toLocalYmd(firstOfMonth), date_to: toLocalYmd(today) });
  }

  function handleExport(): void {
    toast.success(t("t_export_ok"));
  }

  const drawerOpen = selectedReturnId !== undefined;

  const pager = (
    <Pagination
      total={total}
      offset={offset}
      limit={limit}
      pageSizeOptions={[20, 50, 100]}
      onChange={({ offset: nextOffset, limit: nextLimit }) =>
        onUrlFilterChange({ ...urlFilter, offset: nextOffset, limit: nextLimit })
      }
    />
  );

  return (
    <div data-testid="returns-page">
      <PageHeader title={t("title_returns")} />

      {/* KPI cards */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <KpiCard label={t("nav_returns")} value={total} />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <input
            key={urlFilter.search ?? ""}
            type="search"
            placeholder={t("search_returns")}
            defaultValue={urlFilter.search ?? ""}
            onBlur={handleSearchCommit}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearchCommit(e);
            }}
            className="w-full rounded-lg border border-[var(--premium-border)] bg-[var(--surface-raised)] px-3 py-1.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-subtle)] focus:outline-none focus-ring"
            aria-label={t("search_returns")}
          />
        </div>

        {/* Marketplace select */}
        <div className="relative">
          <select
            value={urlFilter.marketplace ?? "all"}
            onChange={handleMarketplaceChange}
            className="rounded-lg border border-[var(--premium-border)] bg-[var(--surface-raised)] px-3 py-1.5 pr-8 text-sm text-[var(--text-primary)] focus:outline-none focus-ring appearance-none cursor-pointer"
            aria-label={t("f_all_mkp")}
          >
            <option value="all">{t("f_all_mkp")}</option>
            {(Object.keys(MARKETPLACE_LABEL) as Marketplace[]).map((mkp) => (
              <option key={mkp} value={mkp}>
                {MARKETPLACE_LABEL[mkp]}
              </option>
            ))}
          </select>
        </div>

        {/* Date range inputs */}
        <label className="flex items-center gap-1 text-sm text-[var(--text-secondary)]">
          {t("f_date_from")}
          <input
            type="date"
            value={urlFilter.date_from ?? ""}
            onChange={handleDateFromChange}
            data-testid="returns-date-from"
            aria-label={t("f_date_from")}
            className="h-9 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-2 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          />
        </label>
        <label className="flex items-center gap-1 text-sm text-[var(--text-secondary)]">
          {t("f_date_to")}
          <input
            type="date"
            value={urlFilter.date_to ?? ""}
            onChange={handleDateToChange}
            data-testid="returns-date-to"
            aria-label={t("f_date_to")}
            className="h-9 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-2 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          />
        </label>

        {/* Date presets */}
        <button
          type="button"
          onClick={handlePresetToday}
          data-testid="returns-date-today"
          className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
        >
          {t("f_date_today")}
        </button>
        <button
          type="button"
          onClick={handlePreset7d}
          data-testid="returns-date-7d"
          className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
        >
          {t("f_date_7d")}
        </button>
        <button
          type="button"
          onClick={handlePresetMonth}
          data-testid="returns-date-month"
          className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
        >
          {t("f_date_month")}
        </button>

        {/* Clear button */}
        {hasActiveReturnFilters(urlFilter) && (
          <button
            type="button"
            onClick={handleClear}
            data-testid="returns-filter-clear"
            className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
          >
            <X className="h-4 w-4" aria-hidden />
            {t("btn_clear_filters")}
          </button>
        )}

        {/* Export button (ghost/secondary — NOT primary) */}
        <button
          type="button"
          onClick={handleExport}
          className="flex items-center gap-1.5 rounded-lg border border-[var(--premium-border)] bg-[var(--surface-raised)] px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] transition hover:bg-[var(--canvas)] focus-ring"
          aria-label={t("btn_export")}
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          {t("btn_export")}
        </button>
      </div>

      {pager}

      {/* Table or loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16" data-testid="returns-loading">
          <Spinner size="lg" label={t("loading_orders")} />
        </div>
      ) : (
        <ReturnsTable returns={returns} onOpen={setSelectedReturnId} />
      )}

      {pager}

      {/* Return detail drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setSelectedReturnId(undefined)}
        title={t("return_detail")}
        subtitle={selectedReturnId}
        width={540}
      >
        {selectedReturnId && <DrawerContent returnId={selectedReturnId} />}
      </Drawer>
    </div>
  );
}
